import axios from 'axios';
import { handleApiError, retryWithBackoff, ErrorTypes } from '../utils/errorHandler';

// Create axios instance with default config. `withCredentials` is required
// so the browser sends the httpOnly refresh-token cookie on /auth/refresh
// and /auth/logout calls.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'),
  withCredentials: true,
});

// Function to set auth token
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

// Function to remove auth token
export const removeAuthToken = () => {
  delete api.defaults.headers.common['Authorization'];
};

// Hit /auth/refresh to rotate the refresh cookie and pick up a new access
// token. Returns the new token on success or null on failure. Concurrent
// callers share the same in-flight request so one transient 401 doesn't
// kick off N refreshes.
let refreshInFlight = null;
export const refreshAccessToken = async () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(
        `${api.defaults.baseURL}/api/auth/refresh`,
        {},
        { withCredentials: true }
      )
      .then((res) => res.data?.token || null)
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Add request interceptor for timeout and retry configuration
api.interceptors.request.use(
  (config) => {
    // Set default timeout if not specified
    if (!config.timeout) {
      config.timeout = 30000; // 30 seconds default timeout
    }
    return config;
  },
  (error) => {
    handleApiError(error, 'Request Interceptor');
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token errors and logging
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const url = originalRequest.url || '';

    // On 401 from an authenticated request, try once to rotate via /refresh
    // and replay the original. Skip /auth/ endpoints themselves so we never
    // recurse — those failing means the session is actually dead.
    const isAuthEndpoint = url.includes('/api/auth/');
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        setAuthToken(newToken);
        // Tell the React tree to pick up the new in-memory token.
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token: newToken } }));
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api.request(originalRequest);
      }
      // Refresh failed → session is gone. Sweep memory + redirect.
      removeAuthToken();
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }

    const errorInfo = handleApiError(error, `${error.config?.method?.toUpperCase()} ${error.config?.url}`);
    error.standardized = errorInfo;

    return Promise.reject(error);
  }
);

// Helper function to handle API calls with error handling
const apiCall = async (fn, context, useRetry = false) => {
  try {
    if (useRetry) {
      return await retryWithBackoff(fn);
    }
    return await fn();
  } catch (error) {
    // The error is already logged by the interceptor. Re-throw with the
    // standardized friendly message, but preserve `response` / `status` so
    // callers can still branch on HTTP semantics (e.g. 401 → "wrong password").
    if (error.standardized) {
      const wrapped = new Error(error.standardized.message);
      wrapped.response = error.response;
      wrapped.status = error.response?.status;
      wrapped.standardized = error.standardized;
      throw wrapped;
    }
    throw error;
  }
};

// Authentication service
export const authService = {
  login: async (username, password) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/auth/login', { username, password });
        return response.data;
      },
      'Login'
    );
  },
  
  register: async (username, password, inviteCode) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/auth/register', { username, password, inviteCode });
        return response.data;
      },
      'Register'
    );
  },
  
  changePassword: async (currentPassword, newPassword) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/auth/change-password', {
          currentPassword,
          newPassword
        });
        return response.data;
      },
      'Change Password'
    );
  },

  logout: async () => {
    // Best-effort — never block the UI on logout. Even if the request fails
    // we still drop the local session.
    try { await api.post('/api/auth/logout'); } catch (_) {}
    removeAuthToken();
  },
};

// Character service
export const characterService = {
  getAllCharacters: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/characters');
        return response.data;
      },
      'Get All Characters',
      true // Use retry for GET requests
    );
  },
  
  getCharacterById: async (id) => {
    return apiCall(
      async () => {
        const response = await api.get(`/api/characters/${id}`);
        return response.data;
      },
      `Get Character ${id}`,
      true
    );
  },
  
  getCharacterQuestions: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/characters/questions');
        return response.data;
      },
      'Get Character Questions',
      true
    );
  },
  
  createCharacter: async (formData) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/characters/create', formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
          },
          timeout: 180000 // 180 second timeout for character creation with AI (3 minutes)
        });
        return response.data;
      },
      'Create Character'
    );
  },
  
  updateCharacter: async (id, formData) => {
    return apiCall(
      async () => {
        const response = await api.put(`/api/characters/${id}`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      },
      `Update Character ${id}`
    );
  },
  
  deleteCharacter: async (id) => {
    return apiCall(
      async () => {
        const response = await api.delete(`/api/characters/${id}`);
        return response.data;
      },
      `Delete Character ${id}`
    );
  },
  
  getCharacterImageUrl: (id) => {
    const baseURL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
    return `${baseURL}/api/characters/${id}/image`;
  },
};

// Session service
export const sessionService = {
  getAllSessions: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/sessions');
        return response.data;
      },
      'Get All Sessions',
      true
    );
  },
  
  getAllPublicSessions: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/sessions/all');
        return response.data;
      },
      'Get Public Sessions',
      true
    );
  },
  
  getSessionsByCharacter: async (characterId) => {
    return apiCall(
      async () => {
        const response = await api.get(`/api/sessions/character/${characterId}`);
        return response.data;
      },
      `Get Sessions for Character ${characterId}`,
      true
    );
  },

  getAllAdminSessions: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/sessions/admin/all');
        return response.data;
      },
      'Get All Admin Sessions',
      true
    );
  },
  
  getPublicSession: async (sessionId) => {
    return apiCall(
      async () => {
        const response = await api.get(`/api/sessions/view/${sessionId}`);
        return response.data;
      },
      `Get Public Session ${sessionId}`,
      true
    );
  },
  
  getSessionById: async (id) => {
    return apiCall(
      async () => {
        const response = await api.get(`/api/sessions/${id}`);
        return response.data;
      },
      `Get Session ${id}`,
      true
    );
  },
  
  createSession: async (name, characterId) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/sessions', { name, characterId });
        return response.data;
      },
      'Create Session'
    );
  },
  
  deleteSession: async (id) => {
    return apiCall(
      async () => {
        const response = await api.delete(`/api/sessions/${id}`);
        return response.data;
      },
      `Delete Session ${id}`
    );
  },
};

// Chat service
export const chatService = {
  sendMessage: async (sessionId, message) => {
    return apiCall(
      async () => {
        // Long timeout: a cold Ollama model load on the backend can take minutes
        // on the first message. Must be >= the ingress and app→LiteLLM timeouts.
        const response = await api.post(`/api/chat/${sessionId}`, { message }, { timeout: 600000 });
        return response.data;
      },
      `Send Message to Session ${sessionId}`,
      true // Retry on network failures for chat messages
    );
  },
};

// Admin service
export const adminService = {
  getUsers: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/admin/users');
        return response.data;
      },
      'Get Users',
      true
    );
  },
  
  updateUserRole: async (userId, role) => {
    return apiCall(
      async () => {
        const response = await api.put(`/api/admin/users/${userId}/role`, { role });
        return response.data;
      },
      `Update User ${userId} Role`
    );
  },
  
  deleteUser: async (userId) => {
    return apiCall(
      async () => {
        const response = await api.delete(`/api/admin/users/${userId}`);
        return response.data;
      },
      `Delete User ${userId}`
    );
  },

  resetUserPassword: async (userId) => {
    return apiCall(
      async () => {
        const response = await api.post(`/api/admin/users/${userId}/reset-password`, {});
        return response.data;
      },
      `Reset Password for User ${userId}`
    );
  },

  getLlmModels: async () => {
    return apiCall(
      async () => {
        const response = await api.get('/api/admin/llm/models');
        return response.data;
      },
      'Get LLM Models',
      true
    );
  },

  setActiveLlmModel: async (model) => {
    return apiCall(
      async () => {
        const response = await api.post('/api/admin/llm/active-model', { model });
        return response.data;
      },
      `Set Active LLM Model ${model}`
    );
  },
};

// Export the api instance for direct use if needed
export { api };

// Re-export error handling utilities for use in components
export { ErrorTypes, getUserFriendlyMessage, handleApiError } from '../utils/errorHandler';
